import type { JSX } from 'react';

/**
 * LanguageToggle — EN/ES language selector (controlled component).
 *
 * WCAG 2.1 AA: keyboard accessible, aria-pressed, role="group".
 * State and localStorage persistence are owned by AppShell — this component
 * is fully controlled: receives current `lang` as a prop and calls `onLangChange`
 * when the user switches. No internal state.
 */
const LANGS = [
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
] as const;

export type LangCode = (typeof LANGS)[number]['code'];

interface LanguageToggleProps {
  lang: LangCode;
  onLangChange?: (lang: LangCode) => void;
}

export function LanguageToggle({ lang, onLangChange }: LanguageToggleProps): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Language selection"
      className="inline-flex gap-0.5 rounded-full border border-[var(--line)] bg-white p-0.5"
    >
      {LANGS.map(({ code, label }) => (
        <button
          key={code}
          type="button"
          aria-pressed={lang === code}
          onClick={() => onLangChange?.(code)}
          className="cursor-pointer rounded-full px-3 py-1 text-sm font-bold text-[var(--forest)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)] aria-pressed:bg-[var(--forest)] aria-pressed:text-white"
        >
          {label}
        </button>
      ))}
    </div>
  );
}
